import { useRef, useState } from 'react'
import { Box, Button, CircularProgress, IconButton } from '@mui/material'
import { AttachFile, Close, VideoFile } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { apiClient } from '@/services/apiClient'

export interface UploadedMedia {
  id: string
  url: string
  fileName: string
  contentType: string
}

interface MediaUploaderProps {
  uploads: UploadedMedia[]
  onChange: (uploads: UploadedMedia[]) => void
  disabled?: boolean
}

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo'
const MAX_SIZE_MB = 100

export default function MediaUploader({ uploads, onChange, disabled }: MediaUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return

    const newUploads: UploadedMedia[] = []
    setUploading(true)

    try {
      for (const file of Array.from(files)) {
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
          enqueueSnackbar(`${file.name} exceeds ${MAX_SIZE_MB} MB limit`, { variant: 'warning' })
          continue
        }
        try {
          const result = await apiClient.uploadMedia(file)
          newUploads.push(result)
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Unknown error'
          enqueueSnackbar(`Failed to upload ${file.name}: ${message}`, { variant: 'error' })
        }
      }
      if (newUploads.length > 0) {
        onChange([...uploads, ...newUploads])
      }
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const handleRemove = (index: number) => {
    onChange(uploads.filter((_, i) => i !== index))
  }

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        style={{ display: 'none' }}
        onChange={e => handleFiles(e.target.files)}
        disabled={disabled || uploading}
      />

      {uploads.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {uploads.map((upload, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                width: 80,
                height: 80,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
              }}
            >
              {upload.contentType.startsWith('image/') ? (
                <Box
                  component="img"
                  src={upload.url}
                  alt={upload.fileName}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: 'action.hover',
                  }}
                >
                  <VideoFile sx={{ fontSize: 32, color: 'text.secondary' }} />
                </Box>
              )}
              <IconButton
                size="small"
                onClick={() => handleRemove(index)}
                disabled={disabled}
                sx={{
                  position: 'absolute', top: 0, right: 0,
                  bgcolor: 'rgba(0,0,0,0.5)', color: 'white',
                  p: 0.25,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Button
        size="small"
        startIcon={uploading ? <CircularProgress size={16} /> : <AttachFile />}
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
        variant="text"
        color="inherit"
        sx={{ textTransform: 'none' }}
      >
        {uploading ? 'Uploading...' : 'Attach photo/video'}
      </Button>
    </Box>
  )
}
